<template>
  <div class="w-full h-full space-y-4">
    <Card>
      <CardHeader>
        <div class="flex items-center gap-3 text-sm text-muted-foreground mb-1">
          <router-link
            to="/whitelabel/sites"
            class="inline-flex items-center gap-1 hover:text-foreground"
          >
            <ChevronLeft class="w-4 h-4" /> All sites
          </router-link>
          <DropdownMenu v-if="allSites.length > 1">
            <DropdownMenuTrigger
              class="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-muted hover:text-foreground focus:outline-none"
            >
              Switch site <ChevronsUpDown class="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" class="w-56">
              <DropdownMenuLabel>Your sites</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                v-for="s in allSites"
                :key="s.id"
                :class="s.id === siteId ? 'font-medium' : ''"
                @click="switchSite(s.id)"
              >
                <Check
                  class="w-4 h-4 mr-2"
                  :class="s.id === siteId ? 'opacity-100' : 'opacity-0'"
                />
                <span class="truncate">{{ s.name || 'Untitled site' }}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardTitle>
          <input
            v-model="siteName"
            class="bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none w-full max-w-md"
            placeholder="Untitled site"
            @change="renameSite"
            @blur="renameSite"
          />
        </CardTitle>
        <CardDescription>
          Brand and configure this site. Changes are saved as a draft; publish to make them live.
        </CardDescription>
        <CardAction class="flex items-center gap-2">
          <Badge v-if="config.published" variant="success">Published</Badge>
          <Badge v-else variant="secondary">Not published</Badge>
          <Button variant="outline" size="sm" :disabled="saving || loading" @click="saveDraft">
            <Save class="w-4 h-4" /> Save draft
          </Button>
          <Button size="sm" :disabled="publishing || loading" @click="publish">
            <Rocket class="w-4 h-4" /> Publish
          </Button>
        </CardAction>
      </CardHeader>
    </Card>

    <div v-if="loading" class="grid gap-4 lg:grid-cols-2">
      <Skeleton class="h-96" />
      <Skeleton class="h-96" />
    </div>

    <div v-else class="grid gap-4 lg:grid-cols-[420px_minmax(0,1fr)] lg:items-start">
      <!-- Settings sidebar -->
      <Card>
        <CardContent class="pt-6">
          <Tabs default-value="branding">
            <TabsList class="flex flex-wrap">
              <TabsTrigger value="branding">Branding</TabsTrigger>
              <TabsTrigger value="site">Site</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="offers">Offers</TabsTrigger>
              <TabsTrigger value="domain">Domain</TabsTrigger>
            </TabsList>

            <!-- Branding -->
            <TabsContent value="branding" class="space-y-5 pt-4">
              <!-- Theme link (the site's colours come from here, not set per-site) -->
              <div class="rounded-lg border border-border p-3 space-y-2">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <p class="text-sm font-medium">Theme</p>
                    <p class="text-xs text-muted-foreground">
                      This site's brand colours are imported from the theme.
                    </p>
                  </div>
                  <select
                    class="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    :value="config.themeId || ''"
                    :disabled="applyingTheme"
                    @change="applyTheme($event.target.value)"
                  >
                    <option value="">No theme</option>
                    <option v-for="t in themes" :key="t.id" :value="t.id">
                      {{ t.name || 'Untitled theme' }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="grid gap-3 sm:grid-cols-3">
                <div v-for="a in assetFields" :key="a.type" class="grid gap-1.5">
                  <Label>{{ a.label }}</Label>
                  <div
                    class="flex items-center justify-center h-20 rounded-lg border border-dashed border-border bg-muted/30 overflow-hidden"
                  >
                    <img
                      v-if="draft.branding[a.urlKey]"
                      :src="draft.branding[a.urlKey]"
                      class="max-h-16 max-w-full object-contain"
                    />
                    <span v-else class="text-xs text-muted-foreground"
                      >No {{ a.label.toLowerCase() }}</span
                    >
                  </div>
                  <input
                    :ref="`file_${a.type}`"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="(e) => uploadAsset(a.type, e)"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    :disabled="uploading === a.type"
                    @click="$refs[`file_${a.type}`][0].click()"
                  >
                    <Upload class="w-4 h-4" /> {{ uploading === a.type ? 'Uploading…' : 'Upload' }}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <!-- Site -->
            <TabsContent value="site" class="space-y-3 pt-4">
              <div class="grid gap-1.5">
                <Label>Site title</Label>
                <Input v-model="draft.site.siteTitle" placeholder="Acme Rewards" />
              </div>
              <div class="grid gap-1.5">
                <Label>Meta description</Label>
                <Textarea
                  v-model="draft.site.metaDescription"
                  placeholder="Earn cashback with Acme…"
                />
              </div>
              <div class="grid gap-1.5">
                <Label>Hero headline</Label>
                <Input
                  v-model="draft.site.heroTitle"
                  placeholder="Earn cashback on the brands you love"
                />
                <p class="text-xs text-muted-foreground">
                  The big headline on the hero banner. Empty = the site title, or our default.
                </p>
              </div>
              <div class="grid gap-1.5">
                <Label>Hero subtitle</Label>
                <Input
                  v-model="draft.site.heroSubtitle"
                  placeholder="Shop your favourite retailers and get rewarded every time."
                />
              </div>
              <div class="grid gap-1.5">
                <Label>Footer text</Label>
                <Textarea v-model="draft.site.footerText" placeholder="© Acme Ltd" />
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="grid gap-1.5">
                  <Label>Privacy policy URL</Label>
                  <Input
                    v-model="draft.site.privacyPolicyUrl"
                    placeholder="https://acme.com/privacy"
                  />
                </div>
                <div class="grid gap-1.5">
                  <Label>Terms URL</Label>
                  <Input v-model="draft.site.termsUrl" placeholder="https://acme.com/terms" />
                </div>
                <div class="grid gap-1.5">
                  <Label>Cashback terms URL</Label>
                  <Input
                    v-model="draft.site.cashbackTermsUrl"
                    placeholder="https://acme.com/cashback-terms"
                  />
                </div>
                <div class="grid gap-1.5">
                  <Label>Support email</Label>
                  <Input v-model="draft.site.supportEmail" placeholder="support@acme.com" />
                </div>
                <div class="grid gap-1.5">
                  <Label>Support phone</Label>
                  <Input v-model="draft.site.supportPhone" placeholder="+44…" />
                </div>
                <div class="grid gap-1.5">
                  <Label>Support URL</Label>
                  <Input v-model="draft.site.supportUrl" placeholder="https://acme.com/help" />
                </div>
              </div>
            </TabsContent>

            <!-- Features -->
            <TabsContent value="features" class="space-y-1 pt-4">
              <label
                v-for="f in featureFields"
                :key="f.key"
                class="flex items-center justify-between rounded-lg border border-border p-3"
              >
                <div>
                  <p class="text-sm font-medium">{{ f.label }}</p>
                  <p class="text-xs text-muted-foreground">{{ f.hint }}</p>
                </div>
                <Switch
                  :model-value="draft.featureFlags[f.key]"
                  @update:model-value="draft.featureFlags[f.key] = $event"
                />
              </label>
            </TabsContent>

            <!-- Offers -->
            <TabsContent value="offers" class="space-y-4 pt-4">
              <div class="grid gap-1.5">
                <Label>Enabled networks</Label>
                <div class="flex flex-wrap gap-1.5">
                  <Badge
                    v-for="n in networkOptions"
                    :key="n"
                    :variant="draft.offerConfig.enabledNetworks.includes(n) ? 'default' : 'outline'"
                    class="cursor-pointer"
                    @click="toggleIn(draft.offerConfig.enabledNetworks, n)"
                    >{{ n }}</Badge
                  >
                </div>
                <p class="text-xs text-muted-foreground">Empty = all networks enabled.</p>
              </div>

              <div class="grid gap-1.5">
                <Label>Enabled categories</Label>
                <div class="flex flex-wrap gap-1.5">
                  <Badge
                    v-for="cat in categoryOptions"
                    :key="cat"
                    :variant="
                      draft.offerConfig.enabledCategories.includes(cat) ? 'default' : 'outline'
                    "
                    class="cursor-pointer"
                    @click="toggleIn(draft.offerConfig.enabledCategories, cat)"
                    >{{ titleCase(cat) }}</Badge
                  >
                </div>
                <p class="text-xs text-muted-foreground">Empty = all categories shown.</p>
              </div>

              <label class="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p class="text-sm font-medium">Hide expired offers</p>
                  <p class="text-xs text-muted-foreground">
                    Don't show offers past their end date.
                  </p>
                </div>
                <Switch
                  :model-value="draft.offerConfig.hideExpired"
                  @update:model-value="draft.offerConfig.hideExpired = $event"
                />
              </label>
              <label class="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p class="text-sm font-medium">Hide unsupported offers</p>
                  <p class="text-xs text-muted-foreground">
                    Hide offers not compatible with this site.
                  </p>
                </div>
                <Switch
                  :model-value="draft.offerConfig.hideUnsupported"
                  @update:model-value="draft.offerConfig.hideUnsupported = $event"
                />
              </label>
            </TabsContent>

            <!-- Domain -->
            <TabsContent value="domain" class="space-y-4 pt-4">
              <div class="grid gap-1.5">
                <Label>Custom domain</Label>
                <Input v-model="domainForm.customDomain" placeholder="rewards.acme.com" />
                <p class="text-xs text-muted-foreground">
                  Point your own domain at this site. You'll add a DNS record to verify it.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                :disabled="savingDomain || !domainChanged"
                @click="saveDomain"
              >
                <Save class="w-4 h-4" /> Save domain
              </Button>

              <div
                v-if="
                  config.domain &&
                  config.domain.customDomain &&
                  config.domain.status &&
                  config.domain.status !== 'NONE'
                "
                class="rounded-lg border border-border p-3 space-y-3"
              >
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-medium">Status</span>
                  <Badge :variant="config.domain.status === 'ACTIVE' ? 'success' : 'secondary'">{{
                    config.domain.status
                  }}</Badge>
                  <Badge
                    v-if="config.domain.dnsTarget"
                    :variant="dnsVerified ? 'success' : 'secondary'"
                  >
                    DNS:
                    {{
                      dnsVerified
                        ? 'verified'
                        : config.domain.dnsStatus === 'invalid-configuration'
                          ? 'not pointing'
                          : 'pending'
                    }}
                  </Badge>
                  <Badge :variant="config.domain.sslStatus === 'ISSUED' ? 'success' : 'secondary'"
                    >SSL: {{ config.domain.sslStatus }}</Badge
                  >
                  <Button
                    v-if="config.domain.dnsTarget"
                    variant="ghost"
                    size="sm"
                    class="ml-auto"
                    :disabled="refreshingDomain"
                    title="Re-check DNS and SSL with CloudFront"
                    @click="refreshDomainStatus(true)"
                  >
                    <RefreshCw class="w-4 h-4" :class="refreshingDomain ? 'animate-spin' : ''" />
                    Check
                  </Button>
                </div>

                <!-- The DNS record the client must add. With CloudFront tenants this one CNAME both
                     routes the domain and validates the managed SSL certificate. -->
                <!-- Setup instructions are only needed until the DNS check passes. -->
                <div v-if="dnsRecords.length && !dnsVerified" class="text-xs space-y-2">
                  <p class="text-muted-foreground">
                    Add these two records at your DNS provider. We re-check automatically every 30
                    seconds - once both are live, your domain is verified, gets its SSL certificate
                    and starts serving. Nothing else to do.
                  </p>

                  <div
                    v-for="rec in dnsRecords"
                    :key="rec.key"
                    class="rounded-md border border-border overflow-hidden"
                  >
                    <div class="flex items-center justify-between gap-2 bg-muted px-2.5 py-1.5">
                      <span class="font-medium">{{ rec.title }}</span>
                      <span class="text-muted-foreground">{{ rec.hint }}</span>
                    </div>
                    <div
                      class="p-2.5 grid grid-cols-[3.5rem_1fr_auto] gap-x-2 gap-y-1.5 items-center"
                    >
                      <span class="text-muted-foreground">Type</span>
                      <span class="font-mono">{{ rec.type }}</span>
                      <span></span>
                      <span class="text-muted-foreground">Name</span>
                      <span class="font-mono break-all">{{ rec.name }}</span>
                      <button
                        class="justify-self-end text-muted-foreground hover:text-foreground"
                        title="Copy name"
                        @click="copyText(rec.name)"
                      >
                        <Copy class="w-3.5 h-3.5" />
                      </button>
                      <span class="text-muted-foreground">Value</span>
                      <span class="font-mono break-all">{{ rec.value }}</span>
                      <button
                        class="justify-self-end text-muted-foreground hover:text-foreground"
                        title="Copy value"
                        @click="copyText(rec.value)"
                      >
                        <Copy class="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div class="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" @click="copyAllRecords">
                      <Copy class="w-3.5 h-3.5" /> Copy both records
                    </Button>
                    <span v-if="isApexDomain" class="text-muted-foreground">
                      Root domain? Use an ALIAS/ANAME record instead of a CNAME - same name and
                      value.
                    </span>
                  </div>

                  <!-- Migration path: issue the SSL certificate before DNS is switched, by
                       redirecting the validation path from the domain's current server. -->
                  <details
                    v-if="pendingValidationRecords.length"
                    class="rounded-md border border-border"
                  >
                    <summary class="px-2.5 py-1.5 cursor-pointer text-muted-foreground select-none">
                      Already serving traffic on this domain? Get SSL ready before switching DNS
                    </summary>
                    <div class="p-2.5 space-y-2 border-t border-border">
                      <p class="text-muted-foreground">
                        Add a 301 redirect on your current server so your SSL certificate can be
                        issued before you change any DNS:
                      </p>
                      <div
                        v-for="v in pendingValidationRecords"
                        :key="v.domain"
                        class="grid grid-cols-[3.5rem_1fr_auto] gap-x-2 gap-y-1.5 items-center"
                      >
                        <span class="text-muted-foreground">From</span>
                        <span class="font-mono break-all">{{ v.redirectFrom }}</span>
                        <button
                          class="justify-self-end text-muted-foreground hover:text-foreground"
                          title="Copy path"
                          @click="copyText(v.redirectFrom)"
                        >
                          <Copy class="w-3.5 h-3.5" />
                        </button>
                        <span class="text-muted-foreground">To</span>
                        <span class="font-mono break-all">https://{{ v.redirectTo }}</span>
                        <button
                          class="justify-self-end text-muted-foreground hover:text-foreground"
                          title="Copy URL"
                          @click="copyText(`https://${v.redirectTo}`)"
                        >
                          <Copy class="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </details>

                  <p
                    v-if="!dnsVerified && config.domain.dnsStatusReason"
                    class="text-amber-600 dark:text-amber-500"
                  >
                    DNS check: {{ config.domain.dnsStatusReason }}
                  </p>
                </div>
                <p
                  v-else-if="dnsVerified && config.domain.status === 'ACTIVE'"
                  class="text-xs text-green-600 dark:text-green-500"
                >
                  Your domain is live at https://{{ config.domain.customDomain }}.
                </p>
                <p v-else-if="dnsVerified" class="text-xs text-muted-foreground">
                  DNS verified. Your SSL certificate is being issued - this usually takes a few
                  minutes, and your site goes live automatically once it's done. Keep both DNS
                  records in place.
                </p>
                <p
                  v-else-if="!config.domain.dnsTarget"
                  class="text-xs text-amber-600 dark:text-amber-500"
                >
                  No DNS record issued yet - CloudFront provisioning isn't configured on this
                  environment. Re-save the domain once it is.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <!-- Live storefront preview: the real StorefrontPreview component fed this site's
           allocated, config-visible offers - so it shows how it looks live. -->
      <Card class="overflow-hidden lg:sticky lg:top-4">
        <CardHeader class="border-b">
          <div class="flex items-center justify-between gap-2">
            <div>
              <CardTitle class="text-base flex items-center gap-2">
                Live preview
                <span
                  class="text-xs font-normal rounded px-1.5 py-0.5"
                  :class="
                    offersError
                      ? 'bg-destructive/15 text-destructive'
                      : 'bg-muted text-muted-foreground'
                  "
                >
                  {{
                    offersLoading
                      ? 'loading…'
                      : offersError ||
                        `${previewOffers.length} offer${previewOffers.length === 1 ? '' : 's'}`
                  }}
                </span>
              </CardTitle>
              <CardDescription>
                Your allocated offers, themed by this site{{
                  linkedTheme ? ` · ${linkedTheme.name}` : ''
                }}. Includes offers pending approval; the live site shows approved offers only.
              </CardDescription>
              <p
                v-if="!offersLoading && !offersError && !previewOffers.length"
                class="text-xs text-amber-600 dark:text-amber-500 mt-1"
              >
                0 offers matched. Filters on this site: {{ offerFilterSummary }}.
                <button
                  v-if="offerFilterSummary !== 'none'"
                  class="underline font-medium hover:text-amber-700"
                  @click="clearOfferFilters"
                >
                  Clear filters
                </button>
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              :disabled="offersLoading"
              title="Refresh offers"
              @click="fetchPreviewOffers"
            >
              <RefreshCw class="w-4 h-4" :class="offersLoading ? 'animate-spin' : ''" />
            </Button>
          </div>
        </CardHeader>
        <div class="wl-frame">
          <StorefrontPreview
            :config="previewConfig"
            :offers="previewOffers"
            :offers-loading="offersLoading"
          />
        </div>
      </Card>
    </div>
  </div>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Badge,
  Skeleton,
  Input,
  Label,
  Textarea,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  StorefrontPreview,
  toast,
} from '@boogi/ui'
import {
  Save,
  Rocket,
  Upload,
  ChevronLeft,
  ChevronsUpDown,
  Check,
  RefreshCw,
  Copy,
} from 'lucide-vue-next'
import { loadThemeFonts } from '@/utils/fonts'

// Colours and fonts are owned by the theme, never set per-site (imported as a setting).
const THEME_KEYS = [
  'primaryColour',
  'secondaryColour',
  'backgroundColour',
  'textColour',
  'buttonColour',
  'buttonTextColour',
  'darkPrimaryColour',
  'darkSecondaryColour',
  'darkBackgroundColour',
  'darkTextColour',
  'darkButtonColour',
  'darkButtonTextColour',
  'fontFamily',
  'headingFontFamily',
  'fontUrl',
  'componentStyles',
  'customFonts',
  'borderColour',
  'darkBorderColour',
  'showBorders',
]

const OFFER_CATEGORIES = [
  'HEALTH_BEAUTY',
  'ELECTRONICS',
  'FASHION',
  'HOME',
  'LUXURY',
  'SPORTS',
  'TRAVEL',
  'DIGITAL',
  'ACCESSORIES',
  'FOOD_DRINK',
  'SHOPPING',
  'WELLNESS',
  'ENTERTAINMENT',
  'ESSENTIALS',
]

// A blank draft so the form always has objects to bind to before/while loading.
function emptyContent() {
  return {
    branding: { assets: {} },
    site: {},
    featureFlags: {
      search: true,
      categories: true,
      darkMode: true,
      showOfferBanners: false,
      allowIndexing: false,
    },
    offerConfig: {
      enabledNetworks: [],
      enabledCategories: [],
      includedMerchants: [],
      excludedMerchants: [],
      featuredOfferIds: [],
      hideExpired: true,
      hideUnsupported: true,
    },
  }
}

export default {
  name: 'WhitelabelSite',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Skeleton,
    Input,
    Label,
    Textarea,
    Switch,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    StorefrontPreview,
    Save,
    Rocket,
    Upload,
    ChevronLeft,
    ChevronsUpDown,
    Check,
    RefreshCw,
    Copy,
  },
  data() {
    return {
      loading: true,
      saving: false,
      publishing: false,
      savingDomain: false,
      refreshingDomain: false,
      domainStatus: null,
      applyingTheme: false,
      uploading: null,
      config: { domain: {} },
      draft: emptyContent(),
      siteName: '',
      themes: [],
      allSites: [],
      previewOffers: [],
      offersLoading: false,
      offersError: null,
      domainForm: { customDomain: '' },
      categoryOptions: OFFER_CATEGORIES,
      networkOptions: ['INTERNAL', 'CARDLYTICS'],
      assetFields: [
        { type: 'logo', label: 'Logo', urlKey: 'logoUrl' },
        { type: 'favicon', label: 'Favicon', urlKey: 'faviconUrl' },
        { type: 'hero', label: 'Hero', urlKey: 'heroImageUrl' },
      ],
      featureFields: [
        { key: 'search', label: 'Search', hint: 'Let users search offers.' },
        { key: 'categories', label: 'Categories', hint: 'Browse offers by category.' },
        { key: 'darkMode', label: 'Dark mode', hint: 'Offer a dark theme.' },
        {
          key: 'showOfferBanners',
          label: 'Offer banners',
          hint: 'Show offer banner images on cards and brand pages.',
        },
        {
          key: 'allowIndexing',
          label: 'Search engine indexing',
          hint: 'Let Google and other search engines index this site. Off = private.',
        },
      ],
    }
  },
  computed: {
    siteId() {
      return this.$route.params.id
    },
    linkedTheme() {
      return this.themes.find((t) => t.id === this.config.themeId) || null
    },
    dnsVerified() {
      return this.config.domain && this.config.domain.dnsStatus === 'valid-configuration'
    },
    // Nothing to save: the form matches the stored domain (saving would needlessly tear down
    // and re-provision the CloudFront tenant), or both are empty.
    // Enable "Save domain" whenever the entered value differs from what's saved: a typed domain on
    // a new site (differs from empty), a change, or clearing the field to remove a saved domain.
    // Optional chaining keeps it safe if the config hasn't loaded yet.
    domainChanged() {
      const typed = (this.domainForm.customDomain || '').trim().toLowerCase()
      const saved = (this.config?.domain?.customDomain || '').trim().toLowerCase()
      return typed !== saved
    },
    // No subdomain (e.g. acme.com) - most providers won't allow a CNAME there.
    isApexDomain() {
      const d = (this.config.domain && this.config.domain.customDomain) || ''
      return d.split('.').filter(Boolean).length <= 2
    },
    // Certificate validation redirect details - only relevant while the cert is pending, for
    // clients migrating a domain that already serves traffic elsewhere.
    pendingValidationRecords() {
      const s = this.domainStatus
      if (!s || s.certificateStatus !== 'pending-validation') return []
      return (s.certificateValidationRecords || []).filter((v) => v.redirectFrom && v.redirectTo)
    },
    // The two records the client adds, shaped like the rows in a DNS provider's UI.
    dnsRecords() {
      const d = this.config.domain || {}
      if (d.verificationRecordType !== 'CNAME' || !d.dnsTarget || !d.customDomain) return []
      return [
        {
          key: 'point',
          title: '1. Point your domain at us',
          hint: 'Routes traffic and issues your SSL certificate',
          type: this.isApexDomain ? 'ALIAS' : 'CNAME',
          name: d.customDomain,
          value: d.dnsTarget,
        },
        {
          key: 'verify',
          title: '2. Prove you own the domain',
          hint: 'Activates the domain on our network',
          type: 'TXT',
          name: `_cf-challenge.${d.customDomain}`,
          value: d.dnsTarget,
        },
      ]
    },
    // Summary of the site's offer curation (from the draft) — helps explain an empty preview.
    offerFilterSummary() {
      const c = this.draft.offerConfig || {}
      const parts = []
      if (c.enabledNetworks && c.enabledNetworks.length)
        parts.push(`networks=[${c.enabledNetworks.join(',')}]`)
      if (c.enabledCategories && c.enabledCategories.length)
        parts.push(`categories=${c.enabledCategories.length}`)
      if (c.includedMerchants && c.includedMerchants.length)
        parts.push(`included=${c.includedMerchants.length}`)
      if (c.excludedMerchants && c.excludedMerchants.length)
        parts.push(`excluded=${c.excludedMerchants.length}`)
      if (c.hideExpired) parts.push('hideExpired')
      return parts.length ? parts.join(' · ') : 'none'
    },
    // The config handed to the shared StorefrontPreview: colours/fonts from the linked theme
    // (site can override fonts/assets), plus this site's own copy, flags and offer curation.
    previewConfig() {
      const t = this.linkedTheme || {}
      const b = this.draft.branding || {}
      return {
        branding: {
          primaryColour: t.primaryColour,
          secondaryColour: t.secondaryColour,
          backgroundColour: t.backgroundColour,
          textColour: t.textColour,
          buttonColour: t.buttonColour,
          buttonTextColour: t.buttonTextColour,
          darkPrimaryColour: t.darkPrimaryColour,
          darkSecondaryColour: t.darkSecondaryColour,
          darkBackgroundColour: t.darkBackgroundColour,
          darkTextColour: t.darkTextColour,
          darkButtonColour: t.darkButtonColour,
          darkButtonTextColour: t.darkButtonTextColour,
          componentStyles: t.components,
          borderRadius: t.borderRadius,
          offersPerRow: t.offersPerRow,
          borderColour: t.borderColour,
          darkBorderColour: t.darkBorderColour,
          showBorders: t.showBorders,
          overlayColour: t.overlayColour,
          darkOverlayColour: t.darkOverlayColour,
          overlayStrength: t.overlayStrength,
          overlayDarken: t.overlayDarken,
          overlayDirection: t.overlayDirection,
          shadow: t.shadow,
          customFonts: t.customFonts,
          fontFamily: t.fontFamily,
          headingFontFamily: t.headingFontFamily,
          logoUrl: b.logoUrl,
          faviconUrl: b.faviconUrl,
          heroImageUrl: b.heroImageUrl,
        },
        site: this.draft.site,
        featureFlags: this.draft.featureFlags,
        offerConfig: this.draft.offerConfig,
      }
    },
  },
  created() {
    this.load()
  },
  watch: {
    // Load the linked theme's fonts into the document so the editor preview renders them.
    linkedTheme: {
      immediate: true,
      handler(theme) {
        if (theme) loadThemeFonts(theme)
      },
    },
    // Reload when the site switcher navigates to another :id (the component is reused).
    siteId(next, prev) {
      if (next && next !== prev) {
        this.stopDomainPolling()
        this.domainStatus = null
        this.load()
      }
    },
    // Live-update the preview as offer curation (networks/categories/merchants) changes.
    'draft.offerConfig': {
      deep: true,
      handler() {
        if (this.loading) return
        clearTimeout(this._offerCfgTimer)
        this._offerCfgTimer = setTimeout(() => this.fetchPreviewOffers(), 400)
      },
    },
  },
  mounted() {
    document.title = 'Dashboard | Whitelabel site'
  },
  beforeUnmount() {
    this.stopDomainPolling()
    clearTimeout(this._offerCfgTimer)
  },
  methods: {
    base() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },
    authHeaders(json = true) {
      const h = { Authorization: `${localStorage.getItem('token')}` }
      if (json) h['Content-Type'] = 'application/json'
      return h
    },
    api(path = '') {
      return `${this.base()}/private/api/client/whitelabel/sites/${this.siteId}${path}`
    },
    titleCase(v) {
      return String(v)
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    toggleIn(list, value) {
      const i = list.indexOf(value)
      if (i === -1) list.push(value)
      else list.splice(i, 1)
    },
    // Merge server content onto a full empty template so every field is bound.
    hydrate(content) {
      const base = emptyContent()
      if (!content) return base
      return {
        branding: {
          ...base.branding,
          ...(content.branding || {}),
          assets: (content.branding && content.branding.assets) || {},
        },
        site: { ...base.site, ...(content.site || {}) },
        featureFlags: { ...base.featureFlags, ...(content.featureFlags || {}) },
        offerConfig: { ...base.offerConfig, ...(content.offerConfig || {}) },
      }
    },
    applyConfig(config) {
      this.config = config || { domain: {} }
      this.siteName = (config && config.name) || ''
      this.draft = this.hydrate(config && config.draft)
      // Colours and fonts are imported from the theme, not stored per-site - drop any legacy
      // overrides so the theme is the single source (and a later save clears them for good).
      THEME_KEYS.forEach((k) => delete this.draft.branding[k])
      const d = (config && config.domain) || {}
      this.domainForm = { customDomain: d.customDomain || '' }
    },
    // The "Enabled categories" options must be the categories offers are actually tagged with
    // (wizard + enrichment taxonomies), otherwise a selection never matches and the filter does
    // nothing. Falls back to the built-in list if none are available yet.
    async loadCategoryOptions() {
      try {
        const res = await this.$apiFetch(
          `${this.base()}/private/api/client/whitelabel/sites/categories`,
          { method: 'GET', headers: this.authHeaders() }
        )
        if (res.ok) {
          const cats = await res.json()
          if (Array.isArray(cats) && cats.length) this.categoryOptions = cats
        }
      } catch (e) {
        /* keep the fallback OFFER_CATEGORIES */
      }
    },
    async load() {
      this.loading = true
      try {
        const sitesUrl = `${this.base()}/private/api/client/whitelabel/sites`
        const [siteRes, themesRes, sitesRes] = await Promise.all([
          this.$apiFetch(this.api(), { method: 'GET', headers: this.authHeaders() }),
          this.$apiFetch(`${this.base()}/private/api/client/whitelabel/themes`, {
            method: 'GET',
            headers: this.authHeaders(),
          }),
          this.$apiFetch(sitesUrl, { method: 'GET', headers: this.authHeaders() }),
        ])
        if (themesRes.ok) this.themes = await themesRes.json()
        if (sitesRes.ok) this.allSites = await sitesRes.json()
        if (siteRes.ok) this.applyConfig(await siteRes.json())
        else toast.error('Could not load this site.')
        await this.loadCategoryOptions()
      } catch (e) {
        toast.error('Could not load this site.')
      } finally {
        this.loading = false
      }
      this.fetchPreviewOffers()
      this.refreshDomainStatus()
    },
    // The site's allocated, config-visible offers - the real data the storefront would show.
    async fetchPreviewOffers() {
      this.offersLoading = true
      this.offersError = null
      const url = this.api('/preview-offers')
      try {
        // Send the editor's CURRENT (possibly unsaved) offer curation so the preview reflects
        // exactly what's selected in the Offers tab, without needing to save first.
        const res = await this.$apiFetch(url, {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify(this.draft.offerConfig || {}),
        })
        if (res.ok) {
          this.previewOffers = await res.json()
          // eslint-disable-next-line no-console
          console.log(
            '[whitelabel] preview-offers',
            url,
            '->',
            this.previewOffers.length,
            'offers | draft.offerConfig=',
            JSON.parse(JSON.stringify(this.draft.offerConfig || {})),
            '| result=',
            this.previewOffers
          )
        } else {
          const body = await res.text().catch(() => '')
          console.error('[whitelabel] preview-offers failed', res.status, url, body)
          this.offersError = `error ${res.status}`
          toast.error(`Couldn't load preview offers (${res.status}).`)
        }
      } catch (e) {
        console.error('[whitelabel] preview-offers error', e)
        this.offersError = 'network error'
        toast.error("Couldn't load preview offers.")
      } finally {
        this.offersLoading = false
      }
    },
    // Reset the site's offer curation to "show everything", then save + refresh the preview.
    async clearOfferFilters() {
      const oc = this.draft.offerConfig || (this.draft.offerConfig = {})
      oc.enabledNetworks = []
      oc.enabledCategories = []
      oc.includedMerchants = []
      oc.excludedMerchants = []
      await this.saveDraft()
    },
    switchSite(id) {
      if (id && id !== this.siteId) this.$router.push(`/whitelabel/sites/${id}`)
    },
    async renameSite() {
      const name = (this.siteName || '').trim()
      if (!name || name === this.config.name) return
      try {
        const res = await this.$apiFetch(this.api(), {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify({ name }),
        })
        if (res.ok) {
          this.config = await res.json()
        } else toast.error('Could not rename site.')
      } catch (e) {
        toast.error('Could not rename site.')
      }
    },
    async applyTheme(themeId) {
      this.applyingTheme = true
      try {
        const res = await this.$apiFetch(this.api('/theme'), {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify({ themeId: themeId || null }),
        })
        if (res.ok) {
          this.config = await res.json()
          toast.success(themeId ? 'Theme linked' : 'Theme unlinked')
        } else toast.error('Could not update theme.')
      } catch (e) {
        toast.error('Could not update theme.')
      } finally {
        this.applyingTheme = false
      }
    },
    async saveDraft() {
      this.saving = true
      try {
        const res = await this.$apiFetch(this.api('/draft'), {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify(this.draft),
        })
        if (res.ok) {
          this.applyConfig(await res.json())
          toast.success('Draft saved')
          // Offer curation may have changed - refresh the preview's real offers.
          this.fetchPreviewOffers()
        } else toast.error('Failed to save draft.')
      } catch (e) {
        toast.error('Failed to save draft.')
      } finally {
        this.saving = false
      }
    },
    async publish() {
      this.publishing = true
      try {
        // Persist the current edits first, then publish.
        await this.saveDraft()
        const res = await this.$apiFetch(this.api('/publish'), {
          method: 'POST',
          headers: this.authHeaders(),
        })
        if (res.ok) {
          this.applyConfig(await res.json())
          toast.success('Site published')
        } else toast.error('Failed to publish.')
      } catch (e) {
        toast.error('Failed to publish.')
      } finally {
        this.publishing = false
      }
    },
    async uploadAsset(type, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.uploading = type
      try {
        const form = new FormData()
        form.append('type', type)
        form.append('file', file)
        const res = await this.$apiFetch(this.api('/asset'), {
          method: 'POST',
          headers: this.authHeaders(false),
          body: form,
        })
        if (res.ok) {
          this.applyConfig(await res.json())
          toast.success(`${this.titleCase(type)} uploaded`)
        } else toast.error('Upload failed.')
      } catch (e) {
        toast.error('Upload failed.')
      } finally {
        this.uploading = null
      }
    },
    async saveDomain() {
      this.savingDomain = true
      try {
        const params = new URLSearchParams()
        if (this.domainForm.customDomain) params.set('customDomain', this.domainForm.customDomain)
        const res = await this.$apiFetch(this.api(`/domain?${params.toString()}`), {
          method: 'PUT',
          headers: this.authHeaders(),
        })
        if (res.ok) {
          this.applyConfig(await res.json())
          this.domainStatus = null
          toast.success('Domain saved')
          // A tenant may have just been provisioned - pull its DNS/SSL state.
          this.refreshDomainStatus()
        } else {
          // Surface the backend's reason (e.g. a CDN provisioning error) instead of a generic
          // message, so a failed domain save is actually diagnosable.
          const reason = await res.text().catch(() => '')
          toast.error(reason || 'Failed to save domain.')
        }
      } catch (e) {
        toast.error(e?.message || 'Failed to save domain.')
      } finally {
        this.savingDomain = false
      }
    },
    // Live DNS + SSL state, checked with AWS (VerifyDnsConfiguration + managed cert details) via
    // the backend. Auto-polls while verification is in progress; stops once the domain is ACTIVE.
    async refreshDomainStatus(notify = false) {
      const d = this.config.domain
      if (!d || !(d.cloudfrontTenantId || d.dnsTarget)) return
      if (this.refreshingDomain) return
      this.refreshingDomain = true
      try {
        const res = await this.$apiFetch(this.api('/domain/status'), {
          method: 'GET',
          headers: this.authHeaders(),
        })
        if (res.ok) {
          const status = await res.json()
          this.domainStatus = status
          if (status.domain) this.config.domain = status.domain
          if (notify) toast.success('Domain status refreshed')
        } else if (notify) {
          toast.error('Could not check domain status.')
        }
      } catch (e) {
        if (notify) toast.error('Could not check domain status.')
      } finally {
        this.refreshingDomain = false
        this.syncDomainPolling()
      }
    },
    // Keep polling AWS every 30s until DNS is verified and the certificate is issued.
    syncDomainPolling() {
      const d = this.config.domain
      const pending =
        d &&
        (d.cloudfrontTenantId || d.dnsTarget) &&
        !(d.status === 'ACTIVE' && d.sslStatus === 'ISSUED')
      if (pending && !this._domainPollTimer) {
        this._domainPollTimer = setInterval(() => this.refreshDomainStatus(), 30000)
      } else if (!pending && this._domainPollTimer) {
        this.stopDomainPolling()
      }
    },
    stopDomainPolling() {
      clearInterval(this._domainPollTimer)
      this._domainPollTimer = null
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        toast.success('Copied')
      } catch (e) {
        toast.error('Could not copy.')
      }
    },
    // Both records in one paste-able block (zone-file style: name, type, value).
    async copyAllRecords() {
      const text = this.dnsRecords.map((r) => `${r.name}\t${r.type}\t${r.value}`).join('\n')
      try {
        await navigator.clipboard.writeText(text)
        toast.success('Both records copied')
      } catch (e) {
        toast.error('Could not copy.')
      }
    },
  },
}
</script>

<style scoped>
/* A device-like frame so the full-bleed storefront preview scrolls within the card. */
/* Fill the viewport below the page header on tall screens; never shrink below 680px. */
.wl-frame {
  height: max(680px, calc(100vh - 260px));
  overflow: auto;
}
</style>
